import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import {
  getDegreeList,
  createDegree,
  updateDegree,
  deleteDegree,
} from "../api/degree.api";
import type {
  DegreePayload,
  GetDegreeListParams,
} from "@/interface/masterManagement";
import { degreeKeys, STALE_5_MIN } from "@/lib/queryKey";
import { getNextPage, isPaginated } from "@/utils/helper";

export const useDegreeList = (params?: GetDegreeListParams) =>
  useQuery({
    queryKey: degreeKeys.list(params),
    queryFn: () => getDegreeList(params),
    ...STALE_5_MIN,
  });

export const useInfiniteDegreeList = (params?: GetDegreeListParams) => {
  const hasPagination = isPaginated(params);

  return useInfiniteQuery({
    queryKey: degreeKeys.list({ ...params, type: "infinite" }),
    queryFn: ({ pageParam }) =>
      getDegreeList(
        hasPagination ? { ...params, page: pageParam } : params,
      ),
    initialPageParam: params?.page ?? 1,
    getNextPageParam: (lastPage, allPages) =>
      hasPagination
        ? getNextPage(lastPage, allPages, params?.page_size)
        : undefined,
    ...STALE_5_MIN,
  });
};

export const useCreateDegree = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createDegree,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: degreeKeys.lists() });
    },
  });
};

export const useUpdateDegree = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: number | string;
      payload: DegreePayload;
    }) => updateDegree(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: degreeKeys.lists() });
    },
  });
};

export const useDeleteDegree = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number | string) => deleteDegree(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: degreeKeys.lists() });
    },
  });
};
